import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useClaimForm } from '@/hooks/useClaimForm';
import { useClaimDraft } from '@/hooks/useClaimDraft';
import { ClaimWizardStepPolicy } from '@/components/insurance/ClaimWizardStepPolicy';
import { ClaimWizardStepForm } from '@/components/insurance/ClaimWizardStepForm';
import { ClaimWizardStepDocs } from '@/components/insurance/ClaimWizardStepDocs';
import { ClaimWizardStepPayment } from '@/components/insurance/ClaimWizardStepPayment';
import {
  ClaimWizardStepReview,
  ClaimSuccessPanel,
} from '@/components/insurance/ClaimWizardStepReview';
import { ClaimApplicationWizard } from '@/components/insurance/claim-application/ClaimApplicationWizard';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/
import type { IClaimAiAnalysis } from '@/@types/claims';
import type { IClaimFormState } from '@/hooks/useClaimForm';
import type { IFormStep } from '@/@types/policyFormTemplates';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/

const STEP_LABELS = [
  'Select Policy',
  'Fill Details',
  'Upload Documents',
  'Payment Info',
  'Review & Submit',
];

export function NewClaimPage() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  // Need employeeRecordId for claimant resolution — not exposed by useCompanyContext
  const currentMembership = useQuery(api.companies.members.getCurrentMembership);
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draftId');
  const { state, dispatch, nextStep, prevStep, goToStep } = useClaimForm();
  const [successClaimId, setSuccessClaimId] = useState<Id<'claims'> | null>(null);
  const saveDraftRef = useRef<(() => Promise<void>) | null>(null);

  // Load selected policy to get insuranceTypeId and orgId for template query
  // (effectiveOrgId resolved below after selectedPolicyData)
  const selectedPolicyData = useQuery(
    api['insurance/policies'].get,
    state.policyId ? { policyId: state.policyId as Id<'insurancePolicies'> } : 'skip' // AS_JUSTIFIED: policyId selected from active policy list, safe to narrow to typed Id
  );
  const selectedPolicy = selectedPolicyData
    ? { _id: selectedPolicyData._id, insuranceTypeId: selectedPolicyData.insuranceTypeId }
    : undefined;

  // Resolve orgId: org members → currentOrg, company members → company's orgId,
  // simple customers → orgId from selected policy
  const effectiveOrgId =
    currentOrg?._id ?? currentCompany?.orgId ?? selectedPolicyData?.orgId ?? null;

  // Resolve claimant type and ID dynamically based on route context
  const isCompanyRoute = pathname.startsWith('/company');
  const resolvedClaimantType: 'employee' | 'customer' = isCompanyRoute ? 'employee' : 'customer';
  const resolvedClaimantId: string = isCompanyRoute
    ? String(currentMembership?.employeeRecordId ?? '')
    : String(selectedPolicyData?.leadId ?? '');

  // Query for provider-specific claim form template with multi-step wizard
  const claimTemplates = useQuery(
    api['insurance/claimFormTemplates'].list,
    effectiveOrgId && selectedPolicy?.insuranceTypeId
      ? { orgId: effectiveOrgId, insuranceTypeId: selectedPolicy.insuranceTypeId }
      : 'skip'
  );
  const wizardTemplate = claimTemplates?.find(
    (t: { steps?: unknown }) => Array.isArray(t.steps) && t.steps.length > 0
  );

  // Load draft claim for resume (template-driven wizard)
  const draftClaimData = useQuery(
    api.insurance.claims.get,
    draftIdParam ? { claimId: draftIdParam as Id<'claims'> } : 'skip' // AS_JUSTIFIED: draftId URL param from MyClaimsListPage navigation with valid claim ID
  );

  const { isSavingDraft, handleSaveDraft } = useClaimDraft(state, effectiveOrgId, dispatch);

  const handleSelectPolicy = useCallback(
    (policyId: string) => dispatch({ type: 'SET_POLICY', policyId }),
    [dispatch]
  );

  const handleField = useCallback(
    (field: keyof IClaimFormState, value: string) => dispatch({ type: 'SET_FIELD', field, value }),
    [dispatch]
  );

  const handleCustomField = useCallback(
    (fieldKey: string, value: string) => dispatch({ type: 'SET_CUSTOM_FIELD', fieldKey, value }),
    [dispatch]
  );

  const handleAddDoc = useCallback(
    (doc: IClaimFormState['uploadedDocs'][number]) => dispatch({ type: 'ADD_DOC', doc }),
    [dispatch]
  );

  const handlePrefillFromAi = useCallback(
    (data: IClaimAiAnalysis) => dispatch({ type: 'PREFILL_FROM_AI', data }),
    [dispatch]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
    setSuccessClaimId(null);
    goToStep(0);
  }, [dispatch, goToStep]);

  if (authLoading || orgLoading || companyLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <div className="p-8 text-muted-foreground">Please sign in to submit a claim.</div>;
  }

  // effectiveOrgId may be null for simple customers until they select a policy — allow step 0
  const needsOrgId = state.currentStep > 0 && !effectiveOrgId;
  if (needsOrgId) {
    return (
      <div className="p-8 text-muted-foreground">
        No insurance organisation found. Please contact your employer or insurance provider.
      </div>
    );
  }

  // Auto-advance to wizard when resuming a template-driven draft
  const isDraftResume = draftIdParam && draftClaimData?.claimApplicationData;
  const draftPolicyId = draftClaimData?.policyId;

  // If resuming a draft and policy not yet set, set it and advance past step 0
  if (isDraftResume && draftPolicyId && state.policyId !== String(draftPolicyId)) {
    dispatch({ type: 'SET_POLICY', policyId: String(draftPolicyId) });
    goToStep(1);
  }

  // Template-driven wizard takes over the full content area (no old page chrome)
  const useTemplateWizard = (state.currentStep > 0 || isDraftResume) && wizardTemplate;
  if (useTemplateWizard) {
    return (
      <div className="flex flex-col h-full">
        {/* Page header with Cancel + Save Draft */}
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToStep(0)}
              className="gap-2 text-zinc-900"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-base font-bold text-foreground">New Claim</h1>
              <p className="text-xs text-muted-foreground">{wizardTemplate.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/claims`)}>
              Cancel
            </Button>
            <Button variant="outline" size="sm" onClick={() => void saveDraftRef.current?.()}>
              Save Draft
            </Button>
          </div>
        </div>
        <ClaimApplicationWizard
          templateId={wizardTemplate._id}
          templateSteps={wizardTemplate.steps as IFormStep[]} // AS_JUSTIFIED: steps validated as non-empty array above; IFormStep[] matches seed template structure
          templateName={wizardTemplate.name}
          orgId={effectiveOrgId!} // AS_JUSTIFIED: effectiveOrgId is guaranteed non-null when step > 0 (checked above)
          policyId={(draftPolicyId ?? state.policyId) as Id<'insurancePolicies'>} // AS_JUSTIFIED: policyId from draft or policy selection
          existingClaimId={draftIdParam ? (draftIdParam as Id<'claims'>) : undefined} // AS_JUSTIFIED: draftId URL param from MyClaimsListPage
          existingData={
            isDraftResume
              ? (draftClaimData.claimApplicationData as import('@/@types/claims').IClaimApplicationData)
              : undefined
          } // AS_JUSTIFIED: claimApplicationData shape matches IClaimApplicationData from seed template
          claimantType={resolvedClaimantType}
          claimantId={resolvedClaimantId}
          onBack={() => goToStep(0)}
          onComplete={() => navigate(`${basePath}/claims`)}
          onRegisterSaveDraft={fn => {
            saveDraftRef.current = fn;
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-8 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 text-zinc-900"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-bold text-zinc-900">New Claim</h1>
            <p className="text-[13px] text-zinc-500">
              {state.currentStep === 0
                ? 'Select a policy to begin'
                : `Step ${state.currentStep + 1} of ${STEP_LABELS.length}: ${STEP_LABELS[state.currentStep]}`}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-zinc-50">
        {successClaimId ? (
          <div className="mx-auto max-w-2xl p-8">
            <Card>
              <CardContent className="pt-6">
                <ClaimSuccessPanel
                  claimId={successClaimId}
                  basePath={basePath}
                  onReset={handleReset}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="px-8 py-6 space-y-5">
            {state.currentStep === 0 && (
              <ClaimWizardStepPolicy
                orgId={effectiveOrgId as Id<'organisations'>} // AS_JUSTIFIED: orgId unused by component (uses listForPortalUser); may be null for simple customers on step 0
                selectedPolicyId={state.policyId}
                onSelect={handleSelectPolicy}
                onNext={nextStep}
              />
            )}
            {state.currentStep === 1 && effectiveOrgId && (
              <ClaimWizardStepForm
                orgId={effectiveOrgId}
                policyId={state.policyId as Id<'insurancePolicies'>} // AS_JUSTIFIED: policyId selected from active policy list, safe to narrow to typed Id
                insuranceTypeId={
                  selectedPolicy?.insuranceTypeId as Id<'orgInsuranceTypes'> | undefined // AS_JUSTIFIED: insuranceTypeId comes from Convex response; Id type narrowing required
                }
                state={state}
                onField={handleField}
                onCustomField={handleCustomField}
                onAddLineItem={() => dispatch({ type: 'ADD_LINE_ITEM' })}
                onUpdateLineItem={
                  (idx, field, val) =>
                    dispatch({
                      type: 'UPDATE_LINE_ITEM',
                      index: idx,
                      field: field as keyof IClaimFormState['lineItems'][number],
                      value: val,
                    }) // AS_JUSTIFIED: field string from onUpdateLineItem callback narrowed to line item key union
                }
                onRemoveLineItem={idx => dispatch({ type: 'REMOVE_LINE_ITEM', index: idx })}
                onNext={nextStep}
                onPrev={prevStep}
                onSaveDraft={handleSaveDraft}
                isSavingDraft={isSavingDraft}
              />
            )}
            {state.currentStep === 2 && (
              <ClaimWizardStepDocs
                state={state}
                onAddDoc={handleAddDoc}
                onPrefillFromAi={handlePrefillFromAi}
                onNext={nextStep}
                onPrev={prevStep}
                onSaveDraft={handleSaveDraft}
                isSavingDraft={isSavingDraft}
              />
            )}
            {state.currentStep === 3 && (
              <ClaimWizardStepPayment
                state={state}
                onField={handleField}
                onNext={nextStep}
                onPrev={prevStep}
                onSaveDraft={handleSaveDraft}
                isSavingDraft={isSavingDraft}
              />
            )}
            {state.currentStep === 4 && effectiveOrgId && (
              <ClaimWizardStepReview
                orgId={effectiveOrgId}
                state={state}
                onPrev={prevStep}
                onSuccess={claimId => setSuccessClaimId(claimId)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
